import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/admin/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { 
  Plus, 
  FileText, 
  Eye, 
  AlertTriangle,
  CheckCircle,
  MoreHorizontal,
  Download,
  Trash2
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileUploadZone } from "@/components/fleet/FileUploadZone";
import { DatePicker } from "@/components/ui/date-picker";
import { DocumentViewModal } from "@/components/fleet/DocumentViewModal";
import { useFleetVehicles, useFleetDrivers } from "@/hooks/use-fleet-entities";
import { formatDocument } from "@/lib/validations";
import { toast } from "sonner";
import {
  useFleetDocuments,
  useCreateFleetDocument,
  useDeleteFleetDocument,
  useUploadDocument,
  FleetDocument
} from "@/hooks/use-fleet-documents";

const documentTypes = [
  { value: "CRLV", label: "CRLV" },
  { value: "IPVA", label: "IPVA" },
  { value: "insurance", label: "Seguro" },
  { value: "licensing", label: "Licenciamento" },
  { value: "inspection", label: "Inspeção Veicular" },
  { value: "cnh", label: "CNH" },
  { value: "contract", label: "Contrato" },
  { value: "invoice", label: "Nota Fiscal" },
  { value: "other", label: "Outro" },
];

const documentCategories = [
  { value: "vehicle", label: "Veículo" },
  { value: "driver", label: "Motorista" },
  { value: "company", label: "Empresa" },
  { value: "maintenance", label: "Manutenção" },
  { value: "insurance", label: "Seguro" },
];

const documentSchema = z.object({
  name: z.string().min(3, "Nome deve ter pelo menos 3 caracteres"),
  document_type: z.string().min(1, "Selecione o tipo"),
  category: z.enum(['vehicle', 'driver', 'company', 'maintenance', 'insurance']),
  vehicle_id: z.string().optional(),
  driver_id: z.string().optional(),
  reference_number: z.string().optional(),
  authority: z.string().optional(),
  issue_date: z.string().optional(),
  expiry_date: z.string().optional(),
})
.refine(data => {
  if (data.category === 'vehicle') return !!data.vehicle_id;
  return true;
}, { message: "Selecione um veículo", path: ["vehicle_id"] })
.refine(data => {
  if (data.category === 'driver') return !!data.driver_id;
  return true;
}, { message: "Selecione um motorista", path: ["driver_id"] });

export default function FleetDocuments() {
  const { profile } = useAuth();
  const { canCreate, canDelete, isViewer } = useFleetPermissions();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<FleetDocument | null>(null);
  const [viewDocument, setViewDocument] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Get current user's company
  const { data: userCompanies } = useQuery({
    queryKey: ["userCompanies"],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_companies")
        .select("company_id, companies(name)")
        .eq("user_id", profile?.id);
      return data || [];
    },
    enabled: !!profile?.id
  });

  const currentCompanyId = userCompanies?.[0]?.company_id;

  // Get documents and entities
  const { data: documents = [], isLoading } = useFleetDocuments(currentCompanyId);
  const { data: vehicles = [] } = useFleetVehicles(currentCompanyId);
  const { data: drivers = [] } = useFleetDrivers(currentCompanyId);
  
  const createDocument = useCreateFleetDocument(currentCompanyId!);
  const deleteDocument = useDeleteFleetDocument(currentCompanyId!);
  const uploadDocument = useUploadDocument();

  const form = useForm<z.infer<typeof documentSchema>>({
    resolver: zodResolver(documentSchema),
    defaultValues: {
      name: "",
      document_type: "",
      category: "vehicle",
      vehicle_id: "",
      driver_id: "",
      reference_number: "",
      authority: "",
      issue_date: "",
      expiry_date: "",
    },
  });

  const watchCategory = form.watch("category");

  // Calculate document status based on expiry date
  const getDocumentStatus = (doc: FleetDocument) => {
    if (doc.status !== 'active') return 'expired';
    
    if (doc.expiry_date) {
      const today = new Date();
      const expiry = new Date(doc.expiry_date);
      const daysToExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 3600 * 24));
      
      if (daysToExpiry <= 0) return 'expired';
      if (daysToExpiry <= 30) return 'expiring';
    }
    
    return 'valid';
  };

  const getStatusBadge = (status: string, expiryDate?: string) => {
    if (status === "expired") {
      return <Badge variant="destructive">Vencido</Badge>;
    }
    
    if (expiryDate) {
      const today = new Date();
      const expiry = new Date(expiryDate);
      const daysToExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 3600 * 24));
      
      if (daysToExpiry <= 0) {
        return <Badge variant="destructive">Vencido</Badge>;
      } else if (daysToExpiry <= 30) {
        return <Badge variant="secondary">Vencendo</Badge>;
      }
    }
    
    return <Badge variant="default">Válido</Badge>;
  };

  const handleDownload = async (document: FleetDocument) => {
    if (!document.file_path) {
      toast.error("Arquivo não encontrado");
      return;
    }

    try {
      const { data, error } = await supabase.storage
        .from('fleet-documents')
        .download(document.file_path);
      
      if (error) throw error;
      
      const url = URL.createObjectURL(data);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.name;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success("Download iniciado");
    } catch (error) {
      console.error("Erro ao baixar arquivo:", error);
      toast.error("Erro ao baixar arquivo");
    }
  };

  const handleDelete = async (documentId: string) => {
    if (confirm("Tem certeza que deseja excluir este documento?")) {
      deleteDocument.mutate(documentId);
    }
  };

  const onSubmit = async (values: z.infer<typeof documentSchema>) => {
    if (!selectedFile) {
      toast.error("Selecione um arquivo");
      return;
    }

    if (!currentCompanyId) {
      toast.error("Empresa não identificada");
      return;
    }

    setIsUploading(true);

    try {
      // Upload file
      const filePath = `${currentCompanyId}/${Date.now()}_${selectedFile.name}`;
      await uploadDocument.mutateAsync({ file: selectedFile, path: filePath });

      // Create document record
      await createDocument.mutateAsync({
        name: values.name,
        document_type: values.document_type,
        category: values.category,
        vehicle_id: values.vehicle_id || null,
        driver_id: values.driver_id || null,
        file_path: filePath,
        file_size: selectedFile.size,
        mime_type: selectedFile.type,
        issue_date: values.issue_date || null,
        expiry_date: values.expiry_date || null,
        reference_number: values.reference_number || null,
        authority: values.authority || null,
        status: 'active',
      } as any);

      // Reset form and close dialog
      form.reset();
      setSelectedFile(null);
      setIsCreateOpen(false);
      toast.success("Documento criado com sucesso");
    } catch (error) {
      console.error("Erro ao criar documento:", error);
      toast.error("Erro ao criar documento");
    } finally {
      setIsUploading(false);
    }
  };

  const columns: ColumnDef<FleetDocument>[] = [
    {
      accessorKey: "name",
      header: "Nome do Documento",
    },
    {
      accessorKey: "document_type",
      header: "Tipo",
      cell: ({ row }) => {
        const type = documentTypes.find(t => t.value === row.getValue("document_type"));
        return <Badge variant="outline">{type?.label || row.getValue("document_type")}</Badge>;
      },
    },
    {
      accessorKey: "category",
      header: "Categoria",
      cell: ({ row }) => {
        const category = documentCategories.find(c => c.value === row.getValue("category"));
        return <Badge variant="outline">{category?.label || row.getValue("category")}</Badge>;
      },
    },
    {
      header: "Entidade",
      cell: ({ row }) => {
        const doc = row.original;
        if (doc.fleet_vehicles) {
          return (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Veículo</Badge>
              <span className="text-sm">{doc.fleet_vehicles.plate}</span>
            </div>
          );
        } else if (doc.fleet_drivers) {
          return (
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Motorista</Badge>
              <span className="text-sm">{doc.fleet_drivers.name}</span>
            </div>
          );
        }
        return <Badge variant="outline">Empresa</Badge>;
      },
    },
    {
      accessorKey: "expiry_date",
      header: "Data de Vencimento",
      cell: ({ row }) => {
        const date = row.getValue("expiry_date");
        if (!date) return <span className="text-muted-foreground">N/A</span>;
        return new Date(date as string).toLocaleDateString("pt-BR");
      },
    },
    {
      header: "Status",
      cell: ({ row }) => {
        const doc = row.original;
        const status = getDocumentStatus(doc);
        return getStatusBadge(status, doc.expiry_date);
      },
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const document = row.original;
        
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Abrir menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover">
              <DropdownMenuItem onClick={() => setViewDocument(document)}>
                <Eye className="mr-2 h-4 w-4" />
                Visualizar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownload(document)}>
                <Download className="mr-2 h-4 w-4" />
                Download
              </DropdownMenuItem>
              {canDelete && (
                <DropdownMenuItem 
                  onClick={() => handleDelete(document.id)}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Excluir
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  // Filter documents based on active tab
  const filteredDocuments = documents.filter(doc => {
    if (activeTab === "all") return true;
    if (activeTab === "expiring") {
      return getDocumentStatus(doc) === 'expiring';
    }
    if (activeTab === "expired") {
      return getDocumentStatus(doc) === 'expired';
    }
    if (activeTab === "valid") {
      return getDocumentStatus(doc) === 'valid';
    }
    return doc.category === activeTab;
  });

  // Calculate summary data
  const totalDocuments = documents.length;
  const expiringDocuments = documents.filter(doc => getDocumentStatus(doc) === 'expiring').length;
  const expiredDocuments = documents.filter(doc => getDocumentStatus(doc) === 'expired').length;
  const inComplianceDocuments = totalDocuments - expiredDocuments;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Documentos da Frota</h1>
          <p className="text-muted-foreground">
            Gerencie todos os documentos da sua frota
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {isViewer && (
            <Badge variant="secondary">
              👁️ Modo Visualização
            </Badge>
          )}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button disabled={!canCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Documento
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Novo Documento</DialogTitle>
              <DialogDescription>
                Adicione um novo documento à frota
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Informações Básicas */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Informações Básicas</h3>
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nome do Documento *</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex: CRLV 2024" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="document_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo de Documento *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o tipo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {documentTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel>Categoria *</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                            className="flex flex-wrap gap-4"
                          >
                            {documentCategories.map((category) => (
                              <div key={category.value} className="flex items-center space-x-2">
                                <RadioGroupItem value={category.value} id={category.value} />
                                <Label htmlFor={category.value}>{category.label}</Label>
                              </div>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Vinculação */}
                {(watchCategory === 'vehicle' || watchCategory === 'driver') && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold">Vinculação</h3>
                    {watchCategory === 'vehicle' && (
                      <FormField
                        control={form.control}
                        name="vehicle_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Veículo *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione um veículo" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {vehicles.map((vehicle) => (
                                  <SelectItem key={vehicle.id} value={vehicle.id}>
                                    {vehicle.plate} - {vehicle.make} {vehicle.model}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    {watchCategory === 'driver' && (
                      <FormField
                        control={form.control}
                        name="driver_id"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Motorista *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Selecione um motorista" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {drivers.map((driver) => (
                                  <SelectItem key={driver.id} value={driver.id}>
                                    {driver.name} {driver.cpf && `- ${formatDocument(driver.cpf)}`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                )}

                {/* Dados do Documento */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Dados do Documento</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="reference_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Número/Referência</FormLabel>
                          <FormControl>
                            <Input placeholder="Ex: 123456789" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="authority"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Órgão Emissor</FormLabel>
                          <FormControl>
                            <Input placeholder="Ex: DETRAN-SP" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="issue_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Data de Emissão</FormLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Selecione a data"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="expiry_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Data de Vencimento</FormLabel>
                          <FormControl>
                            <DatePicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Selecione a data"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Upload de Arquivo */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">Upload de Arquivo *</h3>
                  <FileUploadZone
                    selectedFile={selectedFile}
                    onFileSelect={setSelectedFile}
                    error={!selectedFile && form.formState.isSubmitted ? "Selecione um arquivo" : undefined}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsCreateOpen(false);
                      form.reset();
                      setSelectedFile(null);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={isUploading}>
                    {isUploading ? "Salvando..." : "Salvar Documento"}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDocuments}</div>
            <p className="text-xs text-muted-foreground">
              documentos cadastrados
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vencendo</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{expiringDocuments}</div>
            <p className="text-xs text-muted-foreground">
              próximos do vencimento
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vencidos</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{expiredDocuments}</div>
            <p className="text-xs text-muted-foreground">
              documentos vencidos
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Em Conformidade</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{inComplianceDocuments}</div>
            <p className="text-xs text-muted-foreground">
              documentos válidos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Documents Table */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">Todos</TabsTrigger>
          <TabsTrigger value="valid">Válidos</TabsTrigger>
          <TabsTrigger value="expiring">Vencendo</TabsTrigger>
          <TabsTrigger value="expired">Vencidos</TabsTrigger>
          <TabsTrigger value="vehicle">Veículos</TabsTrigger>
          <TabsTrigger value="driver">Motoristas</TabsTrigger>
        </TabsList>
        <TabsContent value={activeTab} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Documentos</CardTitle>
              <CardDescription>
                Lista de todos os documentos da frota
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable 
                columns={columns} 
                data={filteredDocuments}
                searchKey="name"
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Document Modal */}
      <DocumentViewModal
        document={viewDocument}
        open={!!viewDocument}
        onOpenChange={(open) => !open && setViewDocument(null)}
      />
    </div>
  );
}
